import {Select, SelectItem} from '@mantine/core';
import React, {useMemo} from 'react';

import getPresetPlateSizes from 'settings/samples/getPresetPlateSizes';
import getTestProps from 'test/lib/cypress/getTestProps';

const NO_PLATE_SIZE_VALUE = '__no_plate_size__';
const CLEAR_PLATE_SIZE_GROUP = 'Clear plate size';
const PLATE_SIZE_GROUP = 'Plate sizes';

function plateSizeValue(height: number, width: number): string {
  return `${height}x${width}`;
}

export function getPlateSizeLabel(height: number, width: number): string {
  return `${height} x ${width} (${height * width} wells)`;
}

interface PlateSizeSelectorProps {
  disabled?: boolean;
  displayName?: string;
  height: number | null;
  onChange: (height: number, width: number) => void;
  onClear?: () => void;
  width: number | null;
}

export default function PlateSizeSelector({
  disabled,
  displayName,
  height,
  onChange,
  onClear,
  width,
}: PlateSizeSelectorProps): JSX.Element {
  const selectedPlateSize = height != null && width != null ? plateSizeValue(height, width) : null;
  const options = useMemo(() => {
    const presetOptions = getPresetPlateSizes().map(([presetHeight, presetWidth]) => ({
      group: onClear != null ? PLATE_SIZE_GROUP : undefined,
      label: getPlateSizeLabel(presetHeight, presetWidth),
      value: plateSizeValue(presetHeight, presetWidth),
    }));
    const selectedSizeIsPreset = presetOptions.some(({value}) => value === selectedPlateSize);
    const selectedSizeOption: Array<SelectItem> =
      height != null && width != null && !selectedSizeIsPreset
        ? [
            {
              group: onClear != null ? PLATE_SIZE_GROUP : undefined,
              label: getPlateSizeLabel(height, width),
              value: plateSizeValue(height, width),
            },
          ]
        : [];
    return [
      ...(onClear == null
        ? []
        : [{group: CLEAR_PLATE_SIZE_GROUP, label: 'No plate size', value: NO_PLATE_SIZE_VALUE}]),
      ...presetOptions,
      ...selectedSizeOption,
    ];
  }, [height, onClear, selectedPlateSize, width]);

  function handleSelect(value: string | null): void {
    if (value === NO_PLATE_SIZE_VALUE) {
      onClear?.();
      return;
    }
    if (value == null) {
      return;
    }
    const [heightText, widthText] = value.split('x');
    const selectedHeight = Number(heightText);
    const selectedWidth = Number(widthText);
    if (
      Number.isInteger(selectedHeight) &&
      Number.isInteger(selectedWidth) &&
      (selectedHeight !== height || selectedWidth !== width)
    ) {
      onChange(selectedHeight, selectedWidth);
    }
  }

  return (
    <Select
      aria-label="Plate size"
      data={options}
      disabled={disabled}
      nothingFound="No plate sizes found"
      onChange={handleSelect}
      placeholder={displayName ?? (onClear != null ? 'No plate size' : 'Select size...')}
      searchable={true}
      size="md"
      styles={(theme) => ({
        separator: {
          '&:first-of-type': {display: 'none'},
          borderTop: `1px solid ${theme.colors.gray[3]}`,
          margin: 0,
          padding: 0,
        },
        separatorLabel: {display: 'none'},
      })}
      value={selectedPlateSize}
      wrapperProps={getTestProps({component: 'PlateSizeSelector', element: 'root'})}
    />
  );
}
